<script setup lang="ts">
/**
 * 「我派出的」:派出去的东西看得见下场,才敢放心委派。
 * 状态唯一判据在工作台(wf_work_items),这里 join 出来只读;撤销走专门端点。
 */
import { onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import PageState from '@/components/PageState.vue';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import { errorMessage, formatDate } from '@/utils/format';

interface SentDelegation {
  id: string;
  content: string;
  owner_name: string | null;
  due_at: string;
  priority: string;
  note: string | null;
  status: string;
  status_changed_at: string | null;
  project_no: string | null;
  project_name: string | null;
  created_at: string;
}

const items = ref<SentDelegation[]>([]);
const loading = ref(true);
const error = ref('');

const STATUS_LABELS: Record<string, string> = {
  pending: '待处理', in_progress: '处理中', completed: '已完成', cancelled: '已撤销',
};

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const result = await eqsApi.sentDelegations();
    items.value = (result.items as SentDelegation[]) ?? [];
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

async function cancel(item: SentDelegation): Promise<void> {
  if (!await confirmAction({ title: `撤销派给 ${item.owner_name ?? '对方'} 的「${item.content.slice(0, 24)}」?` })) return;
  try {
    await eqsApi.cancelDelegation(Number(item.id));
    notify('success', '已撤销', '对方会收到撤销通知。');
    await load();
  } catch (caught) {
    notify('error', '撤销失败', errorMessage(caught));
  }
}

function overdue(item: SentDelegation): boolean {
  return !['completed', 'cancelled'].includes(item.status) && Date.parse(item.due_at) < Date.now();
}

onMounted(load);
defineExpose({ load });
</script>

<template>
  <PageState v-if="loading" state="loading" title="正在读取我派出的任务" message="状态与责任人工作台实时一致。" />
  <PageState v-else-if="error" state="error" title="我派出的接口不可用" message="稍后重试。" :detail="error" @retry="load" />
  <PageState
    v-else-if="!items.length"
    state="empty"
    title="还没有派出过任务"
    message="点「派活」把要办的事直接派给具体的人——对方工作台会立刻出现,这里可以追踪下场。"
  />
  <table v-else class="data-table sent-table">
    <thead>
      <tr>
        <th>内容</th><th>责任人</th><th>期限</th><th>状态</th><th>项目</th><th></th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="item in items" :key="item.id" :class="{ 'is-done': ['completed', 'cancelled'].includes(item.status) }">
        <td>
          <strong>{{ item.content }}</strong>
          <small v-if="item.note">{{ item.note }}</small>
        </td>
        <td>{{ item.owner_name ?? '—' }}</td>
        <td :class="{ 'is-overdue': overdue(item) }">
          {{ formatDate(item.due_at) }}<b v-if="overdue(item)">逾期</b>
        </td>
        <td>
          {{ STATUS_LABELS[item.status] ?? item.status }}
          <small v-if="item.status_changed_at && item.status !== 'pending'">{{ formatDate(item.status_changed_at) }}</small>
        </td>
        <td>{{ item.project_no ?? '—' }}</td>
        <td>
          <button
            v-if="!['completed', 'cancelled'].includes(item.status)"
            class="inline-button inline-button-danger"
            type="button"
            @click="cancel(item)"
          >撤销</button>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
.sent-table td strong { display: block; font-size: 13px; }
.sent-table td small { display: block; margin-top: 2px; color: var(--muted); font-size: 12px; }
.sent-table .is-done td { color: var(--muted); }
.sent-table .is-overdue b {
  margin-left: 6px; padding: 1px 6px; border-radius: 999px;
  background: rgba(196, 63, 56, .1); color: var(--risk); font-size: 11px;
}
</style>
